// Shared color-tile rendering for a customized line item — used everywhere a cart/order line can
// appear (cart dropdown, cart page, checkout order summary, order confirmation/admin order page) so
// the same product always shows the same tiles no matter which page it's on. Tiles alone are used
// where space is tight (the cart dropdown); everywhere else tiles pair with the text description from
// customizationDescription.
export type CustomizationSwatch = {
  component: string;
  colorName: string;
  swatchHex: string | null;
};

export function CustomizationSwatches({ swatches }: { swatches: CustomizationSwatch[] }) {
  if (swatches.length === 0) return null;

  return (
    <span className="customization-swatches" aria-hidden="true">
      {swatches.map((swatch, index) => (
        <span
          key={`${swatch.component}-${index}`}
          className="customization-swatch"
          style={{ background: swatch.swatchHex ?? "#cccccc" }}
          title={swatch.component === "All" ? swatch.colorName : `${swatch.component}: ${swatch.colorName}`}
        />
      ))}
    </span>
  );
}

// The "detailed" rendering — description immediately followed by its own tile, repeated per part
// ("Cap: Blue [tile], Locker: Jade White [tile], ...") rather than all the text first and all the
// tiles after. Used everywhere there's room for the full description (cart page, checkout order
// summary, order confirmation/admin order page); the cart dropdown popup uses CustomizationSwatches
// alone instead since it's too tight for text.
export function CustomizationDetail({ swatches }: { swatches: CustomizationSwatch[] }) {
  if (swatches.length === 0) return null;

  return (
    <span className="customization-description">
      {swatches.map((swatch, index) => (
        <span className="customization-pair" key={`${swatch.component}-${index}`}>
          <span>
            {swatch.component === "All" ? "Color" : swatch.component}: {swatch.colorName}
            {index < swatches.length - 1 ? "," : ""}
          </span>
          <span className="customization-swatch" style={{ background: swatch.swatchHex ?? "#cccccc" }} aria-hidden="true" />
        </span>
      ))}
    </span>
  );
}
